import { collection, doc, writeBatch, getDocs } from 'firebase/firestore';
import { toast } from 'sonner';
import { db } from '../../lib/firebase';
import { LogFunction, EXCLUDED_FIELDS, DefaultValues } from './types';
import { isUserAdmin, convertFromSerializable, applyDefaultValues } from './helpers';
import { validateImportData } from './validation';
import { clearFirestoreData } from './admin';
import { transformInitData } from './transform';
import { 
  setSystemStatus,
  performFullAggregation,
  performFullAlgoliaSync,
  getSystemStatus
} from '../../lib/api';
import { DrinkFlavor, DrinkAttribute } from '../../types/firestore'; // Import necessary types

// Type for the generated IDs map value (can be string or object for drinks)
type GeneratedIdMapValue = string | { firestoreId: string; brandId?: string };
// Type for the generated IDs map
type GeneratedIdMap = Map<string, Map<string, GeneratedIdMapValue>>;

// Helper function to create a composite key for drinks
const getDrinkMapKey = (brandName: string | undefined, drinkName: string | undefined): string | null => {
  try {
    if (!brandName) {
      return null;
    }
    if (!drinkName) {
      return null;
    }
    return `${brandName.toLowerCase()}||${drinkName.toLowerCase()}`;
  } catch (error) {
    console.error("Error creating drink map key:", error);
    console.error("Inputs:", { brandName, drinkName });
    return null;
  }
};

// Helper function to resolve references before writing
const resolveReferences = (
  docData: Record<string, any>,
  collectionName: string,
  generatedIds: GeneratedIdMap,
  logFn?: LogFunction
): Record<string, any> => {
  // Safety check for null input
  if (!docData) {
    const errorMsg = `Null or undefined document data provided to resolveReferences for collection ${collectionName}`;
    logFn?.(errorMsg);
    throw new Error(errorMsg);
  }

  const resolvedData = { ...docData };

  try {
    if (collectionName === 'drinks') {
      // Resolve Brand ID
      const brandName = resolvedData.brandName;
      const brandLookupKey = brandName?.toLowerCase();
      if (brandLookupKey) {
        const brandIdValue = generatedIds.get('brands')?.get(brandLookupKey);
        // Ensure brandIdValue is a string (simple ID lookup)
        if (typeof brandIdValue === 'string') {
          resolvedData.brandId = brandIdValue;
          //logFn?.(`Resolved brand reference for drink '${resolvedData.name}': ${brandName} -> ${brandIdValue}`);
        } else {
          logFn?.(`WARNING: Could not resolve brand reference for drink '${resolvedData.name || 'unknown'}': Brand name '${brandName}' not found or mapping invalid in generated IDs.`);
        }
      } else if (!resolvedData.brandId) { // Only warn if ID isn't already present
        logFn?.(`WARNING: Drink '${resolvedData.name || 'unknown'}' missing brandName for reference resolution.`);
      }

      // Filter out and resolve Flavors
      if (Array.isArray(resolvedData.flavors)) {
        // First filter out null/undefined items
        if (resolvedData.flavors.some((item: unknown) => item === null || item === undefined)) {
          const nullCount = resolvedData.flavors.filter((item: unknown) => item === null || item === undefined).length;
          logFn?.(`WARNING: Drink '${resolvedData.name || 'unknown'}' has ${nullCount} null/undefined flavors that will be filtered out.`);
          resolvedData.flavors = resolvedData.flavors.filter((item: unknown) => item !== null && item !== undefined);
        }
        
        resolvedData.flavors = resolvedData.flavors.map((flavor: Record<string, any>): DrinkFlavor | null => {
          if (!flavor || typeof flavor !== 'object') {
            logFn?.(`WARNING: Invalid flavor item in drink '${resolvedData.name || 'unknown'}'`);
            return null;
          }
          
          if (!flavor.name || typeof flavor.name !== 'string') {
            logFn?.(`WARNING: Flavor missing or invalid name in drink '${resolvedData.name || 'unknown'}'`);
            return null;
          }
          
          const flavorLookupKey = flavor.name.toLowerCase();
          const flavorIdValue = generatedIds.get('flavors')?.get(flavorLookupKey);
          // Ensure flavorIdValue is a string
          if (typeof flavorIdValue === 'string') {
            // Remove groupId if it's not in the DrinkFlavor type to avoid linter errors
            return { id: flavorIdValue, name: flavor.name, groupId: typeof flavor.groupId === 'number' ? flavor.groupId : 0 };
          } else {
            logFn?.(`WARNING: Could not resolve flavor reference for drink '${resolvedData.name || 'unknown'}': Flavor '${flavor.name}' not found or mapping invalid.`);
            return { id: `unresolved_${flavor.name.replace(/\s+/g, '_')}`, name: flavor.name, groupId: typeof flavor.groupId === 'number' ? flavor.groupId : 0 }; // Keep name, mark ID as unresolved
          }
        }).filter((f: DrinkFlavor | null): f is DrinkFlavor => f !== null); // Filter out nulls and add type
      } else {
        // If flavors is missing entirely, add an empty array
        resolvedData.flavors = [];
      }

      // Filter out and resolve Attributes
      if (Array.isArray(resolvedData.attributes)) {
        // First filter out null/undefined items
        if (resolvedData.attributes.some((item: unknown) => item === null || item === undefined)) {
          const nullCount = resolvedData.attributes.filter((item: unknown) => item === null || item === undefined).length;
          logFn?.(`WARNING: Drink '${resolvedData.name || 'unknown'}' has ${nullCount} null/undefined attributes that will be filtered out.`);
          resolvedData.attributes = resolvedData.attributes.filter((item: unknown) => item !== null && item !== undefined);
        }
        
        resolvedData.attributes = resolvedData.attributes.map((attribute: Record<string, any>): DrinkAttribute | null => {
          if (!attribute || typeof attribute !== 'object') {
            logFn?.(`WARNING: Invalid attribute item in drink '${resolvedData.name || 'unknown'}'`);
            return null;
          }
          
          if (!attribute.name || typeof attribute.name !== 'string') {
            logFn?.(`WARNING: Attribute missing or invalid name in drink '${resolvedData.name || 'unknown'}'`);
            return null;
          }
          
          const attributeLookupKey = attribute.name.toLowerCase();
          const attributeIdValue = generatedIds.get('attributes')?.get(attributeLookupKey);
           // Ensure attributeIdValue is a string
          if (typeof attributeIdValue === 'string') {
            // Remove groupId if it's not in the DrinkAttribute type to avoid linter errors
            return { id: attributeIdValue, name: attribute.name, groupId: typeof attribute.groupId === 'number' ? attribute.groupId : 0 };
          } else {
            logFn?.(`WARNING: Could not resolve attribute reference for drink '${resolvedData.name || 'unknown'}': Attribute '${attribute.name}' not found or mapping invalid.`);
            return { id: `unresolved_${attribute.name.replace(/\s+/g, '_')}`, name: attribute.name, groupId: typeof attribute.groupId === 'number' ? attribute.groupId : 0 }; // Keep name, mark ID as unresolved
          }
        }).filter((a: DrinkAttribute | null): a is DrinkAttribute => a !== null); // Filter out nulls and add type
      } else {
        // If attributes is missing entirely, add an empty array
        resolvedData.attributes = [];
      }
    } else if (collectionName === 'sightings') {
        // Resolve Location ID
        const locationName = resolvedData.locationName;
        const locationId = resolvedData.locationId;
        
        // First check if the locationId is already set and valid
        if (locationId) {
            // If locationId is already set, we don't need to resolve it
            // This is the case when importing test data that already has correct IDs
            // Just skip the warning in this case
        } else if (locationName) {
            // WARNING: Changed strategy from before - we can't reliably lookup locations by name
            // anymore since names can be duplicated and we only map by ID.
            // Just warn and leave it empty for default values to handle
            logFn?.(`WARNING: Cannot resolve location by name for sighting with location '${locationName}'. Locations can only be resolved by ID. This sighting will use default/empty location.`);
            resolvedData.locationId = ''; // Set to empty to be filled by default values later
        } else {
             logFn?.(`WARNING: Sighting missing both locationId and locationName for reference resolution.`);
             resolvedData.locationId = ''; // Set to empty to be filled by default values later
        }

        // Resolve Drink ID and Brand ID (if possible) using Drink Name + Brand Name
        const drinkName = resolvedData.drinkName;
        const brandNameForLookup = resolvedData.brandName; // Use brandName from sighting data
        const drinkLookupKey = getDrinkMapKey(brandNameForLookup, drinkName);

        if (drinkLookupKey) {
            const drinkInfoValue = generatedIds.get('drinks')?.get(drinkLookupKey);
            if (typeof drinkInfoValue === 'object' && drinkInfoValue !== null && drinkInfoValue.firestoreId) {
                // We found the drink mapping object
                resolvedData.drinkId = drinkInfoValue.firestoreId; // Set the drink ID
                // logFn?.(`Resolved drink reference for sighting: ${drinkName} -> ${drinkInfoValue.firestoreId}`); // Removed success log

                 // Resolve brand ID using the mapped brandId from the drink, only if sighting doesn't have one
                if (!resolvedData.brandId && drinkInfoValue.brandId) {
                    resolvedData.brandId = drinkInfoValue.brandId;
                    // logFn?.(`Resolved brand reference for sighting via drink '${drinkName}': ${drinkInfoValue.brandId}`); // Removed success log
                 } else if (!resolvedData.brandId && brandNameForLookup) {
                     // If brandId still wasn't resolved via the drink mapping
                     logFn?.(`WARNING: Could not resolve brand reference for sighting '${drinkName || 'unknown'}': Brand ID missing from drink mapping.`);
                  }

            } else {
                logFn?.(`WARNING: Could not resolve drink reference for sighting using key '${drinkLookupKey}'. Mapping invalid or drink not found in generated IDs.`);
                resolvedData.drinkId = ''; // Set to empty to be filled by default values later
            }
        } else if (!resolvedData.drinkId) { // Only warn if ID isn't already present
             logFn?.(`WARNING: Sighting missing drinkName and/or brandName for reference resolution.`);
             resolvedData.drinkId = ''; // Set to empty to be filled by default values later
        }

        // Final check: if brandId is still missing, try resolving via brandName (less ideal)
        if (!resolvedData.brandId && resolvedData.brandName) {
             const brandName = resolvedData.brandName;
             const brandLookupKey = brandName?.toLowerCase();
             const brandIdValue = generatedIds.get('brands')?.get(brandLookupKey);
             if (typeof brandIdValue === 'string') {
                 resolvedData.brandId = brandIdValue;
                 //logFn?.(`Resolved brand reference for sighting via brandName '${brandName}': ${brandIdValue}`);
             } else {
                 logFn?.(`WARNING: Could not resolve brand reference for sighting using backup brandName '${brandName}'.`);
                 resolvedData.brandId = ''; // Set to empty to be filled by default values later
             }
         }

    } else if (collectionName === 'reviews') {
        // Resolve Drink ID
        const drinkName = resolvedData.drinkName;
        const brandNameForLookup = resolvedData.brandName; // Assume review data includes brandName for accurate lookup
        const drinkLookupKey = getDrinkMapKey(brandNameForLookup, drinkName);

        if (drinkLookupKey) {
            const drinkInfoValue = generatedIds.get('drinks')?.get(drinkLookupKey);
             // Only need the firestoreId part here
            if (typeof drinkInfoValue === 'object' && drinkInfoValue !== null && drinkInfoValue.firestoreId) {
                resolvedData.drinkId = drinkInfoValue.firestoreId;
            } else {
                logFn?.(`WARNING: Could not resolve drink reference for review: Drink '${drinkName}' not found or mapping invalid.`);
                resolvedData.drinkId = ''; // Set to empty to be filled by default values later
            }
        } else if (!resolvedData.drinkId) {
             logFn?.(`WARNING: Review missing drinkName for reference resolution.`);
             resolvedData.drinkId = ''; // Set to empty to be filled by default values later
        }

        // Resolve Location ID (optional)
        const locationName = resolvedData.locationName;
        const locationId = resolvedData.locationId;
        
        if (locationId) {
            // If locationId is already set, we don't need to resolve it
            // Skip the warning in this case
        } else if (locationName) {
            // WARNING: Changed strategy - we can't reliably lookup locations by name anymore
            logFn?.(`WARNING: Cannot resolve location by name for review with location '${locationName}'. Locations can only be resolved by ID. This review will have no location reference.`);
            resolvedData.locationId = null; // For reviews, locationId is optional
        }
    } else if (collectionName === 'visitedLocations') {
        // Resolve Location ID
        const locationName = resolvedData.locationName;
        const locationId = resolvedData.locationId;
        
        if (locationId) {
            // ID already set, nothing to do
        } else if (locationName) {
            // WARNING: Changed strategy - we can't reliably lookup locations by name anymore
            logFn?.(`WARNING: Cannot resolve location by name for visitedLocation '${locationName}'. Locations can only be resolved by ID.`);
            resolvedData.locationId = ''; // Set to empty to be filled by default values later
        } else {
            logFn?.(`WARNING: VisitedLocation missing both locationId and locationName.`);
            resolvedData.locationId = ''; // Set to empty to be filled by default values later
        }
    } else if (collectionName === 'confirmations') {
        // Confirmations usually reference the user ID, which may be in generatedIds if users were just created
        const username = resolvedData.username;
        if (username && !resolvedData.userId) {
            const usernameLookupKey = username.toLowerCase();
            const userIdValue = generatedIds.get('users')?.get(usernameLookupKey);
            if (typeof userIdValue === 'string') {
                resolvedData.userId = userIdValue;
            } else {
                logFn?.(`WARNING: Could not resolve user reference for confirmation: User '${username}' not found or mapping invalid.`);
                resolvedData.userId = ''; // Set to empty to be filled by default values later
            }
        } else if (!resolvedData.userId) {
            logFn?.(`WARNING: Confirmation missing userId and couldn't resolve via username.`);
            resolvedData.userId = ''; // Set to empty to be filled by default values later
        }
    } else if (collectionName === 'customMaps') {
        // Resolve location IDs in the locationIds array
        if (Array.isArray(resolvedData.locationIds)) {
            const resolvedLocationIds: string[] = [];
            
            for (const locId of resolvedData.locationIds) {
                // If the ID is already in the proper format, keep it
                if (typeof locId === 'string' && locId.length > 0) {
                    // We no longer try to map locations by name, only by ID
                    // Check if we have a direct ID mapping for this location
                    const locationMap = generatedIds.get('locations');
                    const mappedId = locationMap?.get(locId);
                    
                    if (typeof mappedId === 'string') {
                        resolvedLocationIds.push(mappedId);
                        // logFn?.(`Resolved location ID in customMap: ${locId} -> ${mappedId}`);
                    } else {
                        // Keep the original ID - it might be a direct reference to a location ID
                        resolvedLocationIds.push(locId);
                        logFn?.(`INFO: Using original location ID in customMap: ${locId} (not found in mapping)`);
                    }
                } else {
                    logFn?.(`WARNING: Invalid location ID in customMap: ${locId}`);
                }
            }
            
            resolvedData.locationIds = resolvedLocationIds;
        } else {
            // If locationIds is missing or not an array, initialize it as empty
            resolvedData.locationIds = [];
            logFn?.(`WARNING: customMap missing locationIds array, initializing empty`);
        }
        
        // Make sure ownerId is set if missing
        if (!resolvedData.ownerId && resolvedData.ownerUsername) {
            const usernameLookupKey = resolvedData.ownerUsername.toLowerCase();
            const userIdValue = generatedIds.get('users')?.get(usernameLookupKey);
            if (typeof userIdValue === 'string') {
                resolvedData.ownerId = userIdValue;
                logFn?.(`Resolved owner reference for customMap: ${resolvedData.ownerUsername} -> ${userIdValue}`);
            } else {
                logFn?.(`WARNING: Could not resolve owner reference for customMap: User '${resolvedData.ownerUsername}' not found or mapping invalid.`);
            }
        }
    } else if (collectionName === 'locations') {
      // Check for "store" location type and map to "shop"
      if (resolvedData.locationType === 'store') {
        logFn?.(`INFO: Converting locationType 'store' to 'shop' for location '${resolvedData.name || resolvedData.id}'`);
        resolvedData.locationType = 'shop';
      }
      
      // Other location reference resolving...
    }

    // Remove computed fields for any collection
    const excludedFields = EXCLUDED_FIELDS[collectionName as keyof typeof EXCLUDED_FIELDS] || [];
    for (const field of excludedFields) {
      if (field in resolvedData) {
        delete resolvedData[field];
        logFn?.(`Removed computed field '${field}' from ${collectionName} document`);
      }
    }

    // Normalize timestamp fields - ensure they're properly formatted for Firestore
    const timestampFields = ['createdAt', 'updatedAt', 'lastSightingDate']; 
    for (const field of timestampFields) {
      if (resolvedData[field]) {
        // If it's already a _type: 'timestamp' object, leave it alone
        if (typeof resolvedData[field] === 'object' && resolvedData[field]._type === 'timestamp') {
          // Already in the correct format
        } 
        // If it's an ISO string, convert to a timestamp
        else if (typeof resolvedData[field] === 'string') {
          try {
            const date = new Date(resolvedData[field]);
            if (!isNaN(date.getTime())) {
              resolvedData[field] = {
                _type: 'timestamp',
                seconds: Math.floor(date.getTime() / 1000),
                nanoseconds: (date.getTime() % 1000) * 1000000
              };
            } else {
              // Invalid date string
              logFn?.(`WARNING: Invalid date string in ${field} for ${collectionName} document. Removing field.`);
              delete resolvedData[field];
            }
          } catch (error) {
            logFn?.(`WARNING: Error parsing date in ${field} for ${collectionName} document. Removing field.`);
            delete resolvedData[field];
          }
        }
        // If it's a number (unix timestamp), convert to a timestamp
        else if (typeof resolvedData[field] === 'number') {
          resolvedData[field] = {
            _type: 'timestamp',
            seconds: Math.floor(resolvedData[field] / 1000),
            nanoseconds: (resolvedData[field] % 1000) * 1000000
          };
        }
        // Otherwise it's an invalid format
        else if (typeof resolvedData[field] !== 'object' || !resolvedData[field].seconds) {
          logFn?.(`WARNING: Invalid timestamp format in ${field} for ${collectionName} document. Removing field.`);
          delete resolvedData[field];
        }
      }
    }

  } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logFn?.(`ERROR resolving references for ${collectionName} item ${resolvedData.id || resolvedData.name || 'unknown'}: ${errorMessage}`);
      logFn?.(`Problem document data: ${JSON.stringify(docData)}`);
      throw error; // Re-throw to let caller handle
  }

  return resolvedData;
};

/**
 * Imports data from a JSON structure to Firestore
 * @param jsonData - JSON string of data to import
 * @param clearExisting - Whether to clear existing data before importing
 * @param logFn - Optional function for logging messages during import
 * @param runAggregation - Whether to run aggregation and Algolia sync after import (defaults to true)
 */
export const importFirestoreData = async (jsonData: string, clearExisting: boolean = false, logFn?: LogFunction, runAggregation: boolean = true): Promise<void> => {
  // Check admin status before proceeding
  const isAdmin = await isUserAdmin();
  if (!isAdmin) {
    logFn?.('Permission denied: Admin access required');
    throw new Error('Permission denied: Admin access required');
  }
  
  // Validate the import data before proceeding
  logFn?.('Validating import data structure and content');
  const validationResult = validateImportData(jsonData);
  
  // Log all validation issues
  validationResult.issues.forEach(issue => {
    const prefix = issue.severity === 'error' ? 'ERROR:' : 'WARNING:';
    logFn?.(`${prefix} ${issue.message}`);
  });
  
  // If there are any critical errors, stop the import
  if (!validationResult.valid) {
    const errorCount = validationResult.issues.filter(i => i.severity === 'error').length;
    const warningCount = validationResult.issues.filter(i => i.severity === 'warning').length;
    
    logFn?.(`Validation failed with ${errorCount} errors and ${warningCount} warnings`);
    
    // Show toast with summary and first few errors
    const errorMessages = validationResult.issues
      .filter(i => i.severity === 'error')
      .map(i => i.message)
      .slice(0, 3); // Show first 3 errors
      
    const summaryMessage = errorMessages.length > 0 
      ? `First issues: ${errorMessages.join('; ')}${errorMessages.length < errorCount ? '...' : ''}`
      : '';
      
    toast.error(`Import validation failed: ${errorCount} errors found. ${summaryMessage}`);
    throw new Error(`Import validation failed: ${errorCount} errors found. See logs for details.`);
  }
  
  // If there are warnings but no errors, inform the user but continue
  const warningCount = validationResult.issues.filter(i => i.severity === 'warning').length;
  if (warningCount > 0) {
    logFn?.(`Validation completed with ${warningCount} warnings`);
    toast.warning(`Import data has ${warningCount} warnings. Check logs for details.`);
  } else {
    logFn?.('Validation completed successfully');
  }
  
  try {
    logFn?.(`Parsing JSON data (${jsonData.length} characters)`);
    const data = JSON.parse(jsonData);
    
    // Transform the data initially (e.g., serializable types)
    logFn?.('Performing initial data transformations');
    const transformedData = await transformInitData(data, logFn);
    
    // Check if the structure is using the new nested format with _docData and _subCollections
    const isNewFormat = checkForNestedFormat(transformedData);
    logFn?.(`Detected data format: ${isNewFormat ? 'Nested' : 'Flat'}`);
    
    // Normalize data to a consistent format for further processing
    const normalizedData = isNewFormat 
      ? normalizeNestedData(transformedData, logFn)
      : transformedData;
    
    // Check current system status before disabling
    let currentSystemStatus;
    try {
      currentSystemStatus = await getSystemStatus();
      logFn?.(`Current system status retrieved: Algolia=${currentSystemStatus.algolia?.status}, Aggregation=${currentSystemStatus.aggregation?.status}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logFn?.(`WARNING: Failed to retrieve current system status: ${errorMessage}`);
      currentSystemStatus = { 
        algolia: { status: 'unknown' }, 
        aggregation: { status: 'unknown' } 
      };
    }
    
    // Disable Algolia sync (which also controls aggregation triggers)
    logFn?.('Disabling all triggers (Algolia sync & Firestore aggregations) via syncMode');
    try {
      await setSystemStatus({
        data: {
          algoliaStatus: 'disabled',
          aggregationStatus: 'disabled'
        }
      } as any);
      logFn?.('Successfully set Algolia syncMode and aggregation to disabled (disables all triggers)');
    } catch (error) {
      // If the disable operation fails, abort the entire import process
      const errorMessage = error instanceof Error ? error.message : String(error);
      logFn?.(`CRITICAL ERROR: Failed to disable triggers via syncMode: ${errorMessage}`);
      toast.error(`Import aborted: Failed to disable triggers properly. This prevents potential data corruption.`);
      throw new Error(`Import aborted: Failed to safely disable triggers: ${errorMessage}`);
    }
    
    // If we get here, both triggers were successfully disabled
    
    // Clear existing data if requested
    if (clearExisting) {
      logFn?.('Clearing existing data before import');
      await clearFirestoreData(logFn, false);
    }
    
    // Map to track newly generated IDs mapped from temporary IDs (original ID or name)
    const generatedIds: GeneratedIdMap = new Map<string, Map<string, GeneratedIdMapValue>>();
    
    // Process collections in a specific order to resolve dependencies
    const orderedCollections = [
      'attributes', 'flavors', 'brands', 'locations', 'drinks', 'sightings', 'reviews', 
      'permissions', 'users', 'systemSettings', 'customMaps'
    ];
    
    // Create a separate array for subcollection paths to process after main collections
    const subcollectionPaths: string[] = [];
    
    // Identify subcollection paths first
    for (const key in normalizedData) {
      if (key.includes('/')) {
        subcollectionPaths.push(key);
      }
    }
    
    // Log subcollection paths
    if (subcollectionPaths.length > 0) {
      logFn?.(`Found ${subcollectionPaths.length} subcollection paths to process: ${subcollectionPaths.join(', ')}`);
    }
    
    // Filter to keep only collections that exist in the data
    const collectionsToProcess = orderedCollections.filter(collection => normalizedData[collection]);
    
    // Import data to each collection in order
    for (const collectionName of collectionsToProcess) {
      if (!normalizedData[collectionName]) continue;
      
      logFn?.(`Importing collection: ${collectionName}`);
      let batch = writeBatch(db);
      let batchCount = 0;
      const maxBatchSize = 500; // Firestore batch limit
      let totalDocuments = 0;
      
      // Initialize ID map for this collection if needed
      if (!generatedIds.has(collectionName)) {
        generatedIds.set(collectionName, new Map<string, GeneratedIdMapValue>());
      }
      const collectionIdMap = generatedIds.get(collectionName)!;
      
      // Get existing document IDs to avoid conflicts (only needed for generation, not mapping)
      const existingFirestoreIds = new Set<string>();
      try {
        const existingDocs = await getDocs(collection(db, collectionName));
        existingDocs.forEach(doc => existingFirestoreIds.add(doc.id));
        logFn?.(`Found ${existingFirestoreIds.size} existing Firestore documents in ${collectionName}`);
      } catch (error) {
        logFn?.(`Warning: Failed to fetch existing Firestore IDs for ${collectionName}. Will use collision detection during generation.`);
      }
      
      // Function to generate a unique Firestore ID
      const generateUniqueFirestoreId = (prefix: string = ''): string => {
        let id;
        do {
          // Generate a random ID with an optional prefix
          const randomPart = Math.random().toString(36).substring(2, 10);
          id = prefix ? `${prefix}_${randomPart}` : randomPart;
          // Ensure it doesn't clash with existing Firestore IDs OR temporary mapping keys in this collection
        } while (existingFirestoreIds.has(id) || collectionIdMap.has(id)); // Check against existing IDs and temp keys
        
        existingFirestoreIds.add(id); // Mark as used for Firestore
        return id;
      };
      
      // Common processing function for an item
      const processItem = async (itemData: Record<string, any>, originalId?: string) => {
          try {
              if (!itemData) {
                  throw new Error(`itemData is ${itemData === null ? 'null' : 'undefined'}`);
              }
              
              const currentItemData = { ...itemData }; // Clone to avoid modifying normalizedData directly
              let firestoreDocId = currentItemData.id || originalId; // Use provided ID first
              let tempMappingKey: string | null = null; // Key used to map this item for lookups (lowercase name or original ID)
              
              // Special case for locations without IDs - generate one right away and make sure name is used for mapping
              if (collectionName === 'locations' && !firestoreDocId) {
                  const namePrefix = currentItemData.name ? 
                      currentItemData.name.toLowerCase().replace(/[^a-z0-9]/g, '_').substring(0, 10) : 
                      'loc';
                  firestoreDocId = generateUniqueFirestoreId(namePrefix);
                  currentItemData.id = firestoreDocId; // Set the ID in the item data
                  logFn?.(`Generated new ID '${firestoreDocId}' for location without ID: '${currentItemData.name || 'unnamed'}'`);
              }
              
              // Determine the temporary mapping key (original ID or name.toLowerCase())
              try {
                  if (originalId) { // From object key format
                      tempMappingKey = originalId;
                  } else if (currentItemData.id && typeof currentItemData.id === 'string') { // ID provided in item data (array format)
                      tempMappingKey = currentItemData.id;
                  } else if (currentItemData.name && typeof currentItemData.name === 'string') { // Use name if no ID (array format)
                      // This name alone is NOT the mapping key for drinks!
                      // Set a preliminary key, but override for drinks later.
                      tempMappingKey = currentItemData.name.toLowerCase();
                  }
              } catch (error) {
                  const errorMessage = error instanceof Error ? error.message : String(error);
                  logFn?.(`ERROR determining mapping key: ${errorMessage}. Item data: ${JSON.stringify(currentItemData)}`);
                  throw new Error(`Failed to determine mapping key: ${errorMessage}`);
              }
              
              // Note: For object format, originalId is the key, which might not be lowercase.
              // For array format, if we use name, we lowercase it.
              // Ensure consistency in mapping key selection based on collection type
              try {
                  if (collectionName === 'drinks') {
                      // Drinks will use a composite key set in the special handling section below
                  } else if (collectionName === 'locations') {
                      // For locations, we ONLY map by ID, never by name (since locations can have the same name)
                      if (currentItemData.id) {
                          tempMappingKey = currentItemData.id;
                      } else if (originalId) {
                          tempMappingKey = originalId;
                      } else {
                          // This should never happen now that we generate IDs early for locations
                          tempMappingKey = `location_${firestoreDocId}`;
                      }
                      
                      // Only add the ID-based mapping, do NOT add name-based mappings for locations
                      // This is a change from the previous approach that tried to map by name too
                      collectionIdMap.set(tempMappingKey as string, firestoreDocId);
                      logFn?.(`Mapped location '${currentItemData.name || 'unnamed'}' only by ID '${firestoreDocId}'`);
                  } else if (currentItemData.name && typeof currentItemData.name === 'string') {
                      tempMappingKey = currentItemData.name.toLowerCase(); // Use lowercase name for other collections
                  } else if (originalId) {
                      tempMappingKey = originalId; // Fallback to original key for object format
                  } else if (currentItemData.id) {
                      tempMappingKey = currentItemData.id; // Fallback to provided ID for array format
                  }
              } catch (error) {
                  const errorMessage = error instanceof Error ? error.message : String(error);
                  logFn?.(`ERROR processing collection type mapping: ${errorMessage}. Collection: ${collectionName}, Item data: ${JSON.stringify(currentItemData)}`);
                  throw new Error(`Failed to process collection type mapping: ${errorMessage}`);
              }
              
              // Generate a new Firestore ID if none exists or if the provided one conflicts
              if (!firestoreDocId || existingFirestoreIds.has(firestoreDocId)) {
                  if (firestoreDocId && existingFirestoreIds.has(firestoreDocId)) {
                    logFn?.(`WARNING: ID conflict detected for ${collectionName} item with temp key '${tempMappingKey || 'unknown'}'. Provided ID '${firestoreDocId}' already exists. Generating new ID.`);
                  }
                  const prefix = currentItemData.name ? currentItemData.name.toLowerCase().replace(/[^a-z0-9]/g, '_').substring(0, 10) : collectionName;
                  firestoreDocId = generateUniqueFirestoreId(prefix);
                  logFn?.(`Generated new Firestore ID: ${firestoreDocId} for item with temp key '${tempMappingKey || 'unknown'}' in ${collectionName}`);
              }
              
              // Ensure the final Firestore ID is stored in the data object *before* resolving references
              currentItemData.id = firestoreDocId;
              
              // --- Special handling for drinks before resolving general references ---
              let resolvedBrandIdForDrink: string | undefined = undefined;
              if (collectionName === 'drinks') {
                  try {
                      // Resolve Brand ID *first* for drinks to use in the composite key
                      const brandName = currentItemData.brandName;
                      if (brandName === null || brandName === undefined) {
                          logFn?.(`WARNING: Drink item missing brandName property (value: ${brandName}). Item: ${JSON.stringify(currentItemData)}`);
                      }
                      
                      const brandLookupKey = brandName?.toLowerCase();
                      if (brandLookupKey) {
                          const brandIdValue = generatedIds.get('brands')?.get(brandLookupKey);
                          if (typeof brandIdValue === 'string') {
                              currentItemData.brandId = brandIdValue; // Set the resolved brandId on the item
                              resolvedBrandIdForDrink = brandIdValue;
                              // logFn?.(`Resolved brand reference for drink '${currentItemData.name}' (prior): ${brandName} -> ${brandIdValue}`); // Removed success log
                          } else {
                              logFn?.(`WARNING: Could not resolve brand reference for drink '${currentItemData.name || 'unknown'}' (prior): Brand name '${brandName}' not found or mapping invalid.`);
                          }
                      } else if (!currentItemData.brandId) {
                          logFn?.(`WARNING: Drink '${currentItemData.name || 'unknown'}' missing brandName for prior reference resolution.`);
                      }
                      // Update the tempMappingKey specifically for drinks using the composite key
                      tempMappingKey = getDrinkMapKey(currentItemData.brandName, currentItemData.name);
                      if (!tempMappingKey) {
                          logFn?.(`WARNING: Could not generate composite mapping key for drink '${currentItemData.name || 'unknown'}'. It may not be referenceable.`);
                      }
                  } catch (error) {
                      const errorMessage = error instanceof Error ? error.message : String(error);
                      logFn?.(`ERROR in special drinks handling: ${errorMessage}. Drink data: ${JSON.stringify(currentItemData)}`);
                      throw new Error(`Failed in special drinks handling: ${errorMessage}`);
                  }
              }
              // ---------------------------------------------------------------------
              
              // Resolve references within this item's data *before* storing the mapping
              // This ensures that e.g., a drink's brandId is resolved before we map the drink
              let resolvedItemData;
              try {
                  resolvedItemData = resolveReferences(currentItemData, collectionName, generatedIds, logFn);
              } catch (error) {
                  const errorMessage = error instanceof Error ? error.message : String(error);
                  logFn?.(`ERROR during reference resolution: ${errorMessage}. Collection: ${collectionName}, Item data: ${JSON.stringify(currentItemData)}`);
                  throw new Error(`Reference resolution failed: ${errorMessage}`);
              }

              // Apply default values for missing fields based on collection type
              const completeItemData = applyDefaultValues(resolvedItemData, collectionName as keyof DefaultValues, logFn);
              
              // Prepare final data for Firestore (after resolving internal references & applying defaults)
              const dataToSave = convertFromSerializable(completeItemData);
              
              // At this point tempMappingKey should always be set due to our processing above
              // For drinks, it could be null if getDrinkMapKey returns null, so we'll handle that specific case
              let finalMappingKey = tempMappingKey;
              if (!finalMappingKey) {
                  // If we somehow missed setting a mapping key earlier (shouldn't happen based on logic above)
                  finalMappingKey = `${collectionName}_${firestoreDocId}`;
                  logFn?.(`WARNING: Had to generate fallback mapping key for ${collectionName}: '${finalMappingKey}'`);
              }
              
              // Check for duplicate keys in collection map
              if (collectionIdMap.has(finalMappingKey)) {
                  logFn?.(`WARNING: Duplicate temporary mapping key '${finalMappingKey}' detected in collection '${collectionName}'. Overwriting mapping. Check source data for uniqueness (e.g., case-insensitive names).`);
              }
              
              // Store the mapping based on collection type
              let mapValue: GeneratedIdMapValue;
              if (collectionName === 'drinks') {
                 // For drinks, store the firestoreId and the brandId (resolved earlier)
                 mapValue = { firestoreId: firestoreDocId, brandId: resolvedBrandIdForDrink };
              } else {
                 // For other collections, just store the ID string
                 mapValue = firestoreDocId;
              }
              collectionIdMap.set(finalMappingKey, mapValue);
              
              const docRef = doc(db, collectionName, firestoreDocId);
              batch.set(docRef, dataToSave);
              
              batchCount++;
              totalDocuments++;
              
              // Commit batch when reaching the limit
              if (batchCount >= maxBatchSize) {
                  logFn?.(`Committing batch of ${batchCount} documents for ${collectionName}`);
                  await batch.commit();
                  batch = writeBatch(db); // Create a new batch after committing
                  batchCount = 0;
              }
          } catch (error) {
              const errorMessage = error instanceof Error ? error.message : String(error);
              logFn?.(`CRITICAL ERROR processing item in collection ${collectionName}: ${errorMessage}`);
              logFn?.(`Problem item: ${JSON.stringify(itemData)}`);
              throw new Error(`Failed to process item in ${collectionName}: ${errorMessage}`);
          }
      };
      
      // Check if the collection data is an array or object
      if (Array.isArray(normalizedData[collectionName])) {
        // Handle array format
        for (const item of (normalizedData[collectionName] as any[])) {
          if (item === null || typeof item === 'undefined') {
            logFn?.(`ERROR: Encountered a null or undefined item in collection '${collectionName}' (array item at index ${(normalizedData[collectionName] as any[]).indexOf(item)}). Skipping this item. Please check your import JSON.`);
            continue; 
          }
          await processItem(item); // originalId is derived from item.id or name inside processItem
        }
      } else if (typeof normalizedData[collectionName] === 'object' && normalizedData[collectionName] !== null) {
        // Handle object format (keys are original IDs)
        for (const originalId in (normalizedData[collectionName] as Record<string, any>)) {
          const item = (normalizedData[collectionName] as Record<string, any>)[originalId];
          if (item === null || typeof item === 'undefined') {
            logFn?.(`ERROR: Encountered a null or undefined item in collection '${collectionName}' (original ID: '${originalId}'). Skipping this item. Please check your import JSON.`);
            continue; 
          }
          await processItem(item, originalId);
        }
      }
      
      // Commit any remaining operations
      if (batchCount > 0) {
        logFn?.(`Committing final batch of ${batchCount} documents for ${collectionName}`);
        await batch.commit();
      }
      
      logFn?.(`Imported ${totalDocuments} documents to ${collectionName}. Mapped ${collectionIdMap.size} temporary keys.`);
    }
    
    // --- Process Subcollections ---
    logFn?.('Processing subcollections');
    let subcollectionBatch = writeBatch(db);
    let subcollectionBatchCount = 0;
    const maxBatchSize = 500; // Firestore batch limit
    let totalSubcollectionDocuments = 0;

    // Iterate through all identified subcollection paths
    for (const subcollectionPath of subcollectionPaths) {
      if (!normalizedData[subcollectionPath]) {
        logFn?.(`Skipping subcollection path ${subcollectionPath}: No data found`);
        continue;
      }

      // Validate the path format
      const pathParts = subcollectionPath.split('/');
      if (pathParts.length !== 3) {
        logFn?.(`Warning: Unexpected subcollection path format: ${subcollectionPath}. Expected 'collection/docId/subcollection'. Skipping path.`);
        continue; // Skip this malformed path
      }

      const [parentCollection, parentDocId, subcollectionName] = pathParts;

      // Check if we should use the mapped ID for the parent document
      let mappedParentDocId = parentDocId;
      const parentMap = generatedIds.get(parentCollection);
      if (parentMap && parentMap.has(parentDocId)) {
        const mappedValue = parentMap.get(parentDocId);
        if (typeof mappedValue === 'string') {
          mappedParentDocId = mappedValue;
          logFn?.(`Using mapped parent ID for ${subcollectionPath}: ${parentDocId} -> ${mappedParentDocId}`);
        } else if (mappedValue && typeof mappedValue === 'object' && 'firestoreId' in mappedValue) {
          mappedParentDocId = mappedValue.firestoreId;
          logFn?.(`Using mapped parent ID (from object) for ${subcollectionPath}: ${parentDocId} -> ${mappedParentDocId}`);
        }
      }

      // Process each document *within* this subcollection path
      const subcollectionData = normalizedData[subcollectionPath];
      if (Array.isArray(subcollectionData)) {
        for (const item of subcollectionData) {
          if (typeof item !== 'object' || item === null) {
             logFn?.(`Warning: Skipping invalid item in ${subcollectionPath}: ${JSON.stringify(item)}`);
             continue;
          }

          // Apply reference resolution for the subcollection item
          const resolvedItemData = resolveReferences(item, subcollectionName, generatedIds, logFn);
          
          // Apply default values for missing fields
          const completeItemData = applyDefaultValues(resolvedItemData, subcollectionName as keyof DefaultValues, logFn);

          // Generate ID if needed
          const itemId = completeItemData.id || doc(collection(db, `${parentCollection}/${mappedParentDocId}/${subcollectionName}`)).id;

          // Create the document reference with the correct mapped parent ID
          const subcollectionDocRef = doc(db, parentCollection, mappedParentDocId, subcollectionName, itemId);

          // Prepare data for Firestore
          const dataToSave = convertFromSerializable({ ...completeItemData, id: itemId });

          // Add to the current batch
          subcollectionBatch.set(subcollectionDocRef, dataToSave);
          subcollectionBatchCount++;
          totalSubcollectionDocuments++;

          // Commit when batch gets full
          if (subcollectionBatchCount >= maxBatchSize) {
            logFn?.(`Committing batch of ${subcollectionBatchCount} subcollection documents`);
            await subcollectionBatch.commit();
            subcollectionBatch = writeBatch(db);
            subcollectionBatchCount = 0;
          }
        }
      } else if (typeof subcollectionData === 'object' && subcollectionData !== null) {
        // Handle object format for subcollection data
        for (const docId in (subcollectionData as Record<string, any>)) {
          const item = (subcollectionData as Record<string, any>)[docId];
          
          // Apply reference resolution
          const resolvedItemData = resolveReferences(item, subcollectionName, generatedIds, logFn);
          
          // Apply default values
          const completeItemData = applyDefaultValues(resolvedItemData, subcollectionName as keyof DefaultValues, logFn);

          // Use the provided ID or the key as the document ID
          const itemId = completeItemData.id || docId;

          // Create the document reference with the correct mapped parent ID
          const subcollectionDocRef = doc(db, parentCollection, mappedParentDocId, subcollectionName, itemId);

          // Prepare data for Firestore
          const dataToSave = convertFromSerializable({ ...completeItemData, id: itemId });

          // Add to the current batch
          subcollectionBatch.set(subcollectionDocRef, dataToSave);
          subcollectionBatchCount++;
          totalSubcollectionDocuments++;

          // Commit when batch gets full
          if (subcollectionBatchCount >= maxBatchSize) {
            logFn?.(`Committing batch of ${subcollectionBatchCount} subcollection documents`);
            await subcollectionBatch.commit();
            subcollectionBatch = writeBatch(db);
            subcollectionBatchCount = 0;
          }
        }
      } else {
        logFn?.(`Warning: Subcollection data for ${subcollectionPath} is neither an array nor an object. Skipping.`);
      }
    }

    // Commit any remaining operations in the final batch
    if (subcollectionBatchCount > 0) {
      logFn?.(`Committing final batch of ${subcollectionBatchCount} subcollection documents`);
      await subcollectionBatch.commit();
    }

    logFn?.(`Imported ${totalSubcollectionDocuments} total documents across ${subcollectionPaths.length} subcollection paths.`);
    
    // Run aggregation recalculation after import
    if (runAggregation) {
      logFn?.('Running aggregation recalculation');
      try {
        const aggregationResult = await performFullAggregation();
        logFn?.(`Aggregation completed successfully: ${aggregationResult.success ? 'Success' : 'Failed'} (${aggregationResult.message})`);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logFn?.(`Warning: Aggregation recalculation failed: ${errorMessage}`);
        toast.warning(`Import completed, but failed to recalculate aggregations: ${errorMessage}`);
        // Continue with Algolia sync even if aggregation fails
      }
      
      // Run Algolia sync after aggregation - only if aggregation is enabled
      logFn?.('Syncing data to Algolia');
      try {
        // Using the HTTP endpoint for full Algolia sync as per backend documentation
        const syncResult = await performFullAlgoliaSync();
        logFn?.(`Algolia sync completed ${syncResult.success ? 'successfully' : 'with issues'}`);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logFn?.(`Warning: Algolia sync failed: ${errorMessage}`);
        toast.warning(`Import completed, but failed to sync to Algolia: ${errorMessage}`);
      }
    } else {
      logFn?.('Skipping aggregation recalculation and Algolia sync (disabled by user)');
    }
    
    // Restore previous system status if it was enabled before
    logFn?.('Restoring system status to previous state');
    try {
      if (currentSystemStatus.algolia?.status === 'enabled' || currentSystemStatus.aggregation?.status === 'enabled') {
        // Restore to the previous state
        await setSystemStatus({
          data: {
            algoliaStatus: currentSystemStatus.algolia?.status === 'enabled' ? 'enabled' : 'disabled',
            aggregationStatus: currentSystemStatus.aggregation?.status === 'enabled' ? 'enabled' : 'disabled'
          }
        } as any);
        logFn?.(`System status restored: Algolia=${currentSystemStatus.algolia?.status}, Aggregation=${currentSystemStatus.aggregation?.status}`);
        
        if (currentSystemStatus.algolia?.status === 'enabled') {
          toast.info('Import completed. Algolia sync has been re-enabled. The system will process any queued updates.');
        } else {
          toast.info('Import completed. All triggers remain disabled. Please re-enable manually when ready.');
        }
      } else {
        // Keep triggers disabled
        logFn?.('Keeping triggers disabled after import as they were disabled before');
        toast.info('Import completed. All triggers remain disabled. Please re-enable manually when ready.');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logFn?.(`Warning: Failed to restore system status: ${errorMessage}`);
      toast.warning(`Import completed, but failed to restore system status. Please verify system status manually.`);
    }
    
    logFn?.('Data import completed successfully');
    toast.success("Data imported successfully");
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('Error importing Firestore data:', error);
    logFn?.(`Error importing data: ${errorMessage}`);
    
    // Keep triggers disabled after error - manual re-enabling required
    logFn?.('Keeping triggers disabled after error - manual restart required');
    try {
      await setSystemStatus({
        data: {
          algoliaStatus: 'disabled',
          aggregationStatus: 'disabled'
        }
      } as any);
      logFn?.('Successfully kept triggers disabled via syncMode after error');
      toast.error('Error: All triggers remain disabled after import failure. Please manually re-enable when issues are resolved.');
    } catch (restoreError) {
      const restoreErrorMsg = restoreError instanceof Error ? restoreError.message : String(restoreError);
      logFn?.(`Warning: Failed to disable triggers after error: ${restoreErrorMsg}`);
      toast.error(`Critical: Failed to disable triggers. Please check system status.`);
    }
    
    toast.error(`Failed to import data: ${errorMessage}`);
    throw new Error(`Failed to import data: ${errorMessage}`);
  }
};

/**
 * Checks if the data is using the new nested format with _docData and _subCollections
 */
const checkForNestedFormat = (data: Record<string, unknown>): boolean => {
  // Check a few collections to see if they have the _docData structure
  const sampleCollections = ['drinks', 'locations', 'users'];
  for (const collection of sampleCollections) {
    if (data[collection] && typeof data[collection] === 'object') {
      // Check if it's an object with document keys
      const firstDocKey = Object.keys(data[collection] as Record<string, any>)[0];
      if (firstDocKey && (data[collection] as Record<string, any>)[firstDocKey]?._docData) {
        return true; // Found _docData structure
      }
    }
  }
  return false;
};

/**
 * Normalizes nested data format to a flat format for processing
 */
const normalizeNestedData = (data: Record<string, Record<string, any> | any[]>, logFn?: LogFunction): Record<string, Record<string, any> | any[]> => {
  const result: Record<string, Record<string, any> | any[]> = {};

  // Process each collection
  for (const collectionName in data) {
    if (collectionName.includes('/')) {
      // Skip subcollection paths - will handle them separately
      continue;
    }

    const collectionData = data[collectionName]; // Use a different name to avoid conflict
    if (typeof collectionData !== 'object' || collectionData === null) {
      logFn?.(`Skipping invalid collection: ${collectionName}`);
      continue;
    }

    result[collectionName] = {}; // Initialize as object first

    // Process each document in this collection
    if (Array.isArray(collectionData)) { // Check if it's an array of docs
        result[collectionName] = []; // Re-initialize as array if source is array
        collectionData.forEach((docObj, index) => {
            if (!docObj?._docData) {
                logFn?.(`Warning: Document in ${collectionName} at index ${index} doesn't have _docData property. Skipping.`);
                return;
            }
            const docId = docObj.id || `generated_doc_${index}`;
            (result[collectionName] as any[]).push({ id: docId, ...docObj._docData }); // Push to array

            if (docObj._subCollections) {
                for (const subCollName in docObj._subCollections) {
                    const subCollPath = `${collectionName}/${docId}/${subCollName}`;
                    result[subCollPath] = {}; 
                    for (const subDocId in docObj._subCollections[subCollName]) {
                        (result[subCollPath] as Record<string, any>)[subDocId] = docObj._subCollections[subCollName][subDocId];
                    }
                }
            }
        });
    } else { // Assume it's an object of docs (keyed by docId)
        Object.keys(collectionData).forEach(docId => {
            const docObj = collectionData[docId];
            if (!docObj?._docData) {
                logFn?.(`Warning: Document ${collectionName}/${docId} doesn't have _docData property. Skipping.`);
                return;
            }
            (result[collectionName] as Record<string, any>)[docId] = { ...docObj._docData };

            if (docObj._subCollections) {
                for (const subCollName in docObj._subCollections) {
                    const subCollPath = `${collectionName}/${docId}/${subCollName}`;
                    result[subCollPath] = {}; 
                    for (const subDocId in docObj._subCollections[subCollName]) {
                        (result[subCollPath] as Record<string, any>)[subDocId] = docObj._subCollections[subCollName][subDocId];
                    }
                }
            }
        });
    }
  }

  return result;
}; 